<template>
  <div class="student-center student-tech-center">

    <!-- ══════════════════════════════
         HERO — 我的学习工作台
    ══════════════════════════════ -->
    <section class="student-hero tech-student-hero">
      <!-- 背景装饰 -->
      <div class="sh-grid"></div>
      <div class="sh-glow sh-glow-l"></div>
      <div class="sh-glow sh-glow-r"></div>

      <!-- 左侧文案 -->
      <div class="hero-main">
        <div class="hero-eyebrow">
          <span class="eyebrow-dot"></span>
          我的学习工作台
        </div>
        <h1>欢迎回到<br /><span class="h1-accent">智训立方</span></h1>
        <p class="hero-desc">
          课程学习、案例训练与学习记录统一入口。查看当前学习任务、进入情景课程、回顾历史训练记录。
        </p>
        <div class="quick-actions">
          <button class="qa-primary" @click="$router.push({ name: 'More' })">
            <i class="el-icon-collection"></i>浏览案例库
          </button>
          <button class="qa-ghost" @click="fetchReviewingCourses(); fetchHistoryCourses();">
            <i class="el-icon-refresh"></i>刷新课程
          </button>
        </div>
      </div>

      <!-- 右侧学习状态面板 -->
      <div class="hero-card">
        <div class="hc-header">
          <span class="hc-dot"></span>
          <span class="hc-label">当前账号</span>
        </div>
        <strong class="hc-name">{{ name || userName }}</strong>
        <p class="hc-tip">如遇课程访问、账号权限或学习记录问题，请联系平台管理员或技术支持人员。</p>
        <div class="hc-divider"></div>
        <div class="hc-stats">
          <div class="hcs-item">
            <span class="hcs-label">当前课程</span>
            <strong class="hcs-val">{{ currentCourses.length }}</strong>
          </div>
          <div class="hcs-sep"></div>
          <div class="hcs-item">
            <span class="hcs-label">历史课程</span>
            <strong class="hcs-val">{{ totalItems }}</strong>
          </div>
          <div class="hcs-sep"></div>
          <div class="hcs-item">
            <span class="hcs-label">学习方式</span>
            <strong class="hcs-val hcs-online">在线</strong>
          </div>
        </div>
      </div>
    </section>

    <!-- ══════════════════════════════
         统计条
    ══════════════════════════════ -->
    <section class="dashboard-grid tech-dashboard-grid">
      <div class="stat-card tech-stat-card">
        <div class="sc-icon-wrap">
          <i class="el-icon-video-play"></i>
        </div>
        <div class="sc-body">
          <span>当前学习</span>
          <strong>{{ currentCourses.length }}</strong>
        </div>
        <div class="sc-bar">
          <div class="sc-bar-fill" style="width: 72%"></div>
        </div>
      </div>
      <div class="stat-card tech-stat-card">
        <div class="sc-icon-wrap">
          <i class="el-icon-notebook-2"></i>
        </div>
        <div class="sc-body">
          <span>历史课程</span>
          <strong>{{ totalItems }}</strong>
        </div>
        <div class="sc-bar">
          <div class="sc-bar-fill" style="width: 55%"></div>
        </div>
      </div>
      <div class="stat-card tech-stat-card">
        <div class="sc-icon-wrap">
          <i class="el-icon-connection"></i>
        </div>
        <div class="sc-body">
          <span>学习方式</span>
          <strong>在线</strong>
        </div>
        <div class="sc-bar">
          <div class="sc-bar-fill sc-bar-online" style="width: 100%"></div>
        </div>
      </div>
    </section>

    <!-- ══════════════════════════════
         当前学习
    ══════════════════════════════ -->
    <section class="content-panel tech-content-panel">
      <div class="section-heading-line">
        <div>
          <span class="section-kicker">Current Tasks</span>
          <h2>当前学习</h2>
          <p>继续进行中的课程、考核或情景训练任务。</p>
        </div>
        <span class="panel-count">{{ currentCourses.length }} 项</span>
      </div>

      <div class="course-grid" v-if="currentCourses.length > 0">
        <article
          class="learning-card tech-learning-card"
          v-for="(course, index) in currentCourses"
          :key="index"
          @click="goToCourse(course.urlCode)"
        >
          <!-- 封面 -->
          <div class="course-img">
            <el-image
              :src="baseUrl + course.imgUrl"
              style="width: 100%; height: 100%"
              fit="cover"
            >
              <div slot="error" class="image-slot">
                <div class="img-fallback-icon"><i class="el-icon-film"></i></div>
                <span>课程封面</span>
                <div class="img-fallback-grid"></div>
              </div>
            </el-image>
            <!-- 渐变遮罩 -->
            <div class="img-mask"></div>
            <!-- 状态角标 -->
            <span class="image-label">学习中</span>
            <!-- hover 光条 -->
            <div class="img-glow-bar"></div>
          </div>

          <!-- 内容 -->
          <div class="course-body">
            <div class="course-badge">
              {{ statusText[course.status] || "进行中" }}
            </div>
            <h3>{{ course.title || '未命名课程' }}</h3>
            <div class="course-meta">
              <p><span class="meta-key">教师</span>{{ course.teacher || "智训立方" }}</p>
              <p><span class="meta-key">开始时间</span>{{ course.startDate || "待定" }}</p>
              <p><span class="meta-key">训练方式</span>{{ course.method || "在线学习" }}</p>
            </div>
            <div class="card-action-line">
              <span>继续学习</span>
              <i class="el-icon-right"></i>
            </div>
          </div>
        </article>
      </div>

      <!-- 空状态 -->
      <div v-else class="no-data tech-empty-card">
        <div class="empty-icon-wrap">
          <i class="el-icon-video-play"></i>
        </div>
        <h3>暂无当前学习任务</h3>
        <p>当前账号暂无待学习课程，可进入案例库浏览公开案例资源。</p>
        <button class="qa-primary sm" @click="$router.push({ name: 'More' })">
          进入案例库
        </button>
      </div>
    </section>

    <!-- ══════════════════════════════
         历史课程
    ══════════════════════════════ -->
    <section class="content-panel tech-content-panel history-panel">
      <div class="section-heading-line">
        <div>
          <span class="section-kicker">Learning History</span>
          <h2>历史课程</h2>
          <p>查看已参与课程和历史训练记录。</p>
        </div>
        <span class="panel-count">{{ totalItems }} 项</span>
      </div>

      <div class="course-grid" v-if="historyCourses.length > 0">
        <article
          class="learning-card tech-learning-card history-card"
          v-for="(course, index) in historyCourses"
          :key="index"
          @click="goToCourse(course.urlCode)"
        >
          <div class="course-img">
            <el-image
              :src="baseUrl + course.imgUrl"
              style="width: 100%; height: 100%"
              fit="cover"
            >
              <div slot="error" class="image-slot">
                <div class="img-fallback-icon history-icon"><i class="el-icon-notebook-2"></i></div>
                <span>历史课程</span>
                <div class="img-fallback-grid"></div>
              </div>
            </el-image>
            <div class="img-mask img-mask-dark"></div>
            <span class="image-label label-muted">历史</span>
            <div class="img-glow-bar"></div>
          </div>
          <div class="course-body">
            <div class="course-badge badge-muted">历史记录</div>
            <h3>{{ course.title || '未命名课程' }}</h3>
            <div class="course-meta">
              <p><span class="meta-key">教师</span>{{ course.teacher || "智训立方" }}</p>
              <p><span class="meta-key">开始时间</span>{{ course.startDate || "待定" }}</p>
            </div>
            <div class="card-action-line action-muted">
              <span>查看记录</span>
              <i class="el-icon-right"></i>
            </div>
          </div>
        </article>
      </div>

      <div v-else class="no-data tech-empty-card">
        <div class="empty-icon-wrap history-icon">
          <i class="el-icon-notebook-2"></i>
        </div>
        <h3>暂无历史课程</h3>
        <p>完成课程或训练后，历史记录将在此处展示。</p>
      </div>

      <Pagination
        v-if="totalItems > itemsPerPage"
        :total-items="totalItems"
        :items-per-page="itemsPerPage"
        :current-page="currentPage"
        @page-changed="changeHistoryPage"
      />
    </section>

  </div>
</template>

<script>
import { getContentHistories, getContentAssessments } from "@/api/index";
import { mapState } from "vuex";
import Pagination from "@/components/Pagination";

export default {
  name: "StudentCenter",
  components: {
    Pagination,
  },
  data() {
    return {
      userName: "智训立方用户",
      currentCourses: [],
      historyCourses: [],
      currentPage: 1,
      itemsPerPage: 10,
      goToPageNumber: 1,
      totalItems: 0,
      statusText: {
        ongoing: "进行中",
        review: "待复习",
        completed: "已完成",
      },
    };
  },
  computed: {
    ...mapState(["baseUrl", "name"]),
  },
  methods: {
    fetchReviewingCourses() {
      getContentAssessments({
        pageNum: this.currentPage,
        pageSize: this.itemsPerPage,
      })
        .then((response) => {
          if (response.code == 200) {
            this.currentCourses = response.rows || [];
          }
        })
        .catch((error) => {
          console.error("获取考核课程失败:", error);
        });
    },
    fetchHistoryCourses() {
      getContentHistories({
        pageNum: this.currentPage,
        pageSize: this.itemsPerPage,
      })
        .then((response) => {
          if (response.code == 200) {
            this.totalItems = response.total;
            this.historyCourses = response.rows || [];
          }
        })
        .catch((error) => {
          console.error("获取历史课程失败:", error);
        });
    },
    changeHistoryPage(page) {
      this.currentPage = page;
      this.fetchHistoryCourses();
    },
    goToCourse(urlCode) {
      if (!urlCode) return;
      this.$router.push({
        name: "StudentClass",
        params: { id: urlCode },
      });
    },
  },
  mounted() {
    this.fetchReviewingCourses();
    this.fetchHistoryCourses();
  },
};
</script>

<style scoped lang="scss">
/* ─────────────────────────────────────────
   Design tokens
───────────────────────────────────────── */
.student-tech-center {
  --bd:      #081c42;
  --bm:      #1557d8;
  --ba:      #1e9bff;
  --cyan:    #20c8ff;
  --gray:    #475569;
  --gray-s:  #64748b;
  --card-bg: rgba(255,255,255,0.92);
  --card-bdr: rgba(168,199,245,0.68);

  min-height: 100vh;
  padding: 30px 24px 72px;
  background:
    radial-gradient(ellipse at 7% 0%, rgba(32,200,255,0.09), transparent 30%),
    radial-gradient(ellipse at 94% 4%, rgba(47,111,237,0.12), transparent 32%),
    linear-gradient(180deg, #f2f7ff 0%, #f5f8fd 48%, #ffffff 100%);
}

.student-hero,
.dashboard-grid,
.content-panel {
  width: min(1180px, 100%);
  margin-left: auto;
  margin-right: auto;
}

/* ─────────────────────────────────────────
   HERO
───────────────────────────────────────── */
.tech-student-hero {
  position: relative;
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 24px;
  align-items: stretch;
  padding: 28px 0 24px;
}

/* background decorations */
.sh-grid {
  position: absolute;
  inset: 0;
  opacity: 0.4;
  background-image:
    linear-gradient(rgba(21,87,216,0.09) 1px, transparent 1px),
    linear-gradient(90deg, rgba(21,87,216,0.09) 1px, transparent 1px);
  background-size: 44px 44px;
  mask-image: linear-gradient(180deg, rgba(0,0,0,0.7), transparent 90%);
  pointer-events: none;
}
.sh-glow {
  position: absolute;
  border-radius: 50%;
  filter: blur(14px);
  pointer-events: none;
}
.sh-glow-l { width: 300px; height: 300px; left: -100px; bottom: -80px; background: rgba(21,87,216,0.09); }
.sh-glow-r { width: 220px; height: 220px; right: 0;     top: 0;       background: rgba(32,200,255,0.09); }

/* Left copy */
.hero-main { position: relative; z-index: 1; }

.hero-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 5px 12px;
  border-radius: 999px;
  background: rgba(21,87,216,0.09);
  color: var(--bm);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.3px;
  margin-bottom: 14px;
}
.eyebrow-dot {
  width: 6px; height: 6px;
  border-radius: 50%;
  background: var(--cyan);
  box-shadow: 0 0 0 4px rgba(32,200,255,0.20), 0 0 12px rgba(32,200,255,0.7);
}

.student-hero h1 {
  margin: 0 0 12px;
  color: var(--bd);
  font-size: clamp(26px, 3.2vw, 38px);
  line-height: 1.15;
  font-weight: 950;
  letter-spacing: -0.6px;
}
.h1-accent { color: var(--bm); }

.hero-desc {
  max-width: 680px;
  color: var(--gray);
  font-size: 14px;
  line-height: 1.82;
}

.quick-actions {
  display: flex;
  gap: 10px;
  flex-wrap: nowrap;
  align-items: center;
  margin-top: 20px;
}
.qa-primary, .qa-ghost {
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 0 14px;
  border-radius: 999px;
  border: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 800;
  white-space: nowrap;
  flex: 0 0 auto;
  transition: all 0.22s ease;
}
.qa-primary {
  color: #fff;
  background: linear-gradient(135deg, #1248c8, var(--ba) 62%, var(--cyan));
  box-shadow: 0 10px 22px rgba(21,87,216,0.22);
}
.qa-primary:hover { transform: translateY(-2px); box-shadow: 0 14px 28px rgba(21,87,216,0.30); }
.qa-primary.sm { height: 32px; font-size: 12px; margin-top: 14px; }
.qa-ghost {
  color: var(--bd);
  background: rgba(255,255,255,0.86);
  border: 1px solid rgba(21,87,216,0.16);
  backdrop-filter: blur(10px);
}
.qa-ghost:hover { transform: translateY(-2px); border-color: var(--ba); }

/* Right hero card */
.hero-card {
  position: relative;
  z-index: 1;
  padding: 20px;
  border-radius: 20px;
  background: linear-gradient(150deg, rgba(8,22,58,0.96), rgba(10,52,112,0.92));
  border: 1px solid rgba(32,200,255,0.18);
  color: #fff;
  box-shadow: 0 20px 48px rgba(4,14,44,0.20);
  overflow: hidden;
}
.hero-card::after {
  content: "";
  position: absolute;
  right: -48px; top: -52px;
  width: 138px; height: 138px;
  border-radius: 50%;
  background: rgba(32,200,255,0.14);
  filter: blur(4px);
  pointer-events: none;
}
.hc-header {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 12px;
}
.hc-dot {
  width: 8px; height: 8px;
  border-radius: 50%;
  background: var(--cyan);
  box-shadow: 0 0 10px rgba(32,200,255,0.9);
}
.hc-label {
  font-size: 12px;
  color: rgba(255,255,255,0.65);
  font-weight: 700;
}
.hc-name {
  position: relative;
  z-index: 1;
  display: block;
  font-size: 21px;
  font-weight: 900;
  line-height: 1.2;
  margin-bottom: 10px;
}
.hc-tip {
  position: relative;
  z-index: 1;
  color: rgba(255,255,255,0.70);
  font-size: 12px;
  line-height: 1.68;
}
.hc-divider {
  height: 1px;
  background: rgba(255,255,255,0.10);
  margin: 14px 0;
}
.hc-stats {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 10px;
}
.hcs-item { flex: 1; text-align: center; }
.hcs-label { display: block; font-size: 11px; color: rgba(255,255,255,0.56); margin-bottom: 4px; font-weight: 700; }
.hcs-val   { font-size: 20px; font-weight: 950; color: #fff; line-height: 1; }
.hcs-online { color: #4ade80; }
.hcs-sep   { width: 1px; height: 36px; background: rgba(255,255,255,0.12); }

/* ─────────────────────────────────────────
   DASHBOARD STATS
───────────────────────────────────────── */
.tech-dashboard-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
  /* 与下方内容面板保持同一居中基准，避免 margin 简写覆盖 auto 导致左偏 */
  margin: 20px auto;
}

.tech-stat-card {
  position: relative;
  padding: 16px 18px 14px;
  border-radius: 18px;
  background: var(--card-bg);
  border: 1px solid var(--card-bdr);
  box-shadow: 0 10px 26px rgba(8,28,66,0.07);
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 14px;
}
.sc-icon-wrap {
  width: 42px; height: 42px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  background: linear-gradient(145deg, rgba(21,87,216,0.11), rgba(32,200,255,0.13));
  color: var(--bm);
  font-size: 20px;
  flex-shrink: 0;
}
.sc-body { flex: 1; min-width: 0; }
.sc-body span   { display: block; color: var(--gray-s); font-size: 12px; margin-bottom: 4px; font-weight: 700; }
.sc-body strong { display: block; color: var(--bd); font-size: 22px; font-weight: 950; line-height: 1; }
.sc-bar {
  position: absolute;
  bottom: 0; left: 0; right: 0;
  height: 3px;
  background: rgba(21,87,216,0.08);
}
.sc-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--bm), var(--cyan));
  border-radius: 0 2px 2px 0;
}
.sc-bar-online { background: linear-gradient(90deg, #16a34a, #4ade80); }

/* ─────────────────────────────────────────
   CONTENT PANELS
───────────────────────────────────────── */
.tech-content-panel {
  padding: 22px 24px;
  margin-top: 18px;
  border-radius: 22px;
  background: var(--card-bg);
  border: 1px solid var(--card-bdr);
  box-shadow: 0 16px 40px rgba(8,28,66,0.08);
  backdrop-filter: blur(8px);
}
.history-panel { margin-top: 16px; }

.section-heading-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 18px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(168,199,245,0.4);
}
.section-kicker {
  display: inline-block;
  color: var(--ba);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.5px;
  margin-bottom: 5px;
}
.section-heading-line h2 {
  margin: 0 0 4px;
  color: var(--bd);
  font-size: 20px;
  font-weight: 950;
}
.section-heading-line p {
  margin: 0;
  color: var(--gray-s);
  font-size: 13px;
  line-height: 1.7;
}
.panel-count {
  padding: 5px 11px;
  border-radius: 999px;
  color: var(--bm);
  background: rgba(21,87,216,0.08);
  border: 1px solid rgba(21,87,216,0.14);
  font-size: 12px;
  font-weight: 800;
  white-space: nowrap;
  flex-shrink: 0;
}

/* ─────────────────────────────────────────
   COURSE GRID & CARDS
───────────────────────────────────────── */
.course-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 16px;
}

.tech-learning-card {
  position: relative;
  overflow: hidden;
  border-radius: 18px;
  background: rgba(255,255,255,0.96);
  border: 1px solid var(--card-bdr);
  cursor: pointer;
  transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;
  box-shadow: 0 8px 22px rgba(8,28,66,0.07);
}
.tech-learning-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 20px 44px rgba(8,28,66,0.12);
  border-color: rgba(30,155,255,0.36);
}

/* Cover */
.course-img {
  position: relative;
  height: 148px;
  background: linear-gradient(135deg, #e8f0fc, #f4f8ff);
  overflow: hidden;
}

/* Fallback */
.image-slot {
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: var(--bm);
  font-size: 12px;
  font-weight: 800;
  overflow: hidden;
}
.img-fallback-grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(21,87,216,0.08) 1px, transparent 1px),
    linear-gradient(90deg, rgba(21,87,216,0.08) 1px, transparent 1px);
  background-size: 18px 18px;
  pointer-events: none;
}
.img-fallback-icon {
  position: relative;
  z-index: 1;
  width: 44px; height: 44px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  background: linear-gradient(145deg, rgba(21,87,216,0.12), rgba(32,200,255,0.15));
  border: 1px solid rgba(21,87,216,0.16);
  color: var(--bm);
  font-size: 20px;
}
.img-fallback-icon.history-icon { background: linear-gradient(145deg, rgba(100,116,139,0.12), rgba(148,163,184,0.12)); color: #64748b; border-color: rgba(100,116,139,0.16); }
.image-slot span { position: relative; z-index: 1; }

/* Mask over cover image */
.img-mask {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, transparent 44%, rgba(8,20,52,0.34));
  pointer-events: none;
}
.img-mask-dark { background: linear-gradient(180deg, transparent 44%, rgba(8,20,52,0.50)); }

/* Status badge */
.image-label {
  position: absolute;
  right: 11px; top: 11px;
  z-index: 2;
  padding: 3px 8px;
  border-radius: 999px;
  color: #fff;
  background: rgba(18,72,200,0.88);
  border: 1px solid rgba(255,255,255,0.14);
  backdrop-filter: blur(8px);
  font-size: 11px;
  font-weight: 800;
}
.label-muted { background: rgba(8,28,66,0.66); }

/* Top glow bar on hover */
.img-glow-bar {
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 2px;
  z-index: 4;
  background: linear-gradient(90deg, #1248c8, var(--cyan), rgba(32,200,255,0));
  opacity: 0;
  transition: opacity 0.22s ease;
}
.tech-learning-card:hover .img-glow-bar { opacity: 1; }

/* Card body */
.course-body { padding: 14px 15px 12px; }
.course-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: 999px;
  background: rgba(21,87,216,0.08);
  border: 1px solid rgba(21,87,216,0.14);
  color: var(--bm);
  font-size: 11px;
  font-weight: 800;
  margin-bottom: 9px;
}
.badge-muted {
  background: rgba(100,116,139,0.08);
  border-color: rgba(100,116,139,0.14);
  color: #64748b;
}
.course-body h3 {
  margin: 0 0 9px;
  color: var(--bd);
  font-size: 15px;
  line-height: 1.38;
  font-weight: 900;
  height: 42px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.course-meta p {
  display: flex;
  gap: 5px;
  margin: 4px 0;
  color: var(--gray);
  font-size: 12px;
  line-height: 1.5;
}
.meta-key {
  color: var(--bd);
  font-weight: 700;
  white-space: nowrap;
  min-width: 48px;
}
.card-action-line {
  margin-top: 11px;
  padding-top: 10px;
  border-top: 1px solid rgba(226,232,240,0.8);
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: var(--bm);
  font-size: 12px;
  font-weight: 900;
  transition: color 0.2s;
}
.tech-learning-card:hover .card-action-line { color: var(--ba); }
.action-muted { color: #64748b; }

/* Empty state */
.no-data.tech-empty-card {
  min-height: 190px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 32px;
  border-radius: 18px;
  background: rgba(248,251,255,0.96);
  border: 1px dashed rgba(21,87,216,0.18);
}
.empty-icon-wrap {
  width: 54px; height: 54px;
  border-radius: 18px;
  display: grid;
  place-items: center;
  background: linear-gradient(145deg, rgba(21,87,216,0.10), rgba(32,200,255,0.12));
  color: var(--ba);
  font-size: 24px;
  margin-bottom: 14px;
}
.empty-icon-wrap.history-icon { background: linear-gradient(145deg, rgba(100,116,139,0.10), rgba(148,163,184,0.10)); color: #64748b; }
.tech-empty-card h3 { color: var(--bd); font-size: 16px; font-weight: 900; margin-bottom: 6px; }
.tech-empty-card p  { color: var(--gray-s); font-size: 13px; line-height: 1.72; }

/* ─────────────────────────────────────────
   RESPONSIVE
───────────────────────────────────────── */
@media (max-width: 900px) {
  .tech-student-hero    { grid-template-columns: 1fr; }
  .tech-dashboard-grid  { grid-template-columns: 1fr; gap: 10px; }
  .hero-card            { display: none; }
}
@media (max-width: 640px) {
  .student-tech-center  { padding: 22px 16px 56px; }
  .student-hero h1      { font-size: 26px; }
  .section-heading-line { display: block; }
  .panel-count          { display: inline-flex; margin-top: 10px; }
  .tech-content-panel   { padding: 16px; border-radius: 18px; }
  .course-grid          { grid-template-columns: 1fr; }
}
</style>


/* V2.0D action alignment fix: desktop buttons stay in one clean row; mobile wraps gracefully */
@media (max-width: 640px) {
  .quick-actions {
    flex-wrap: wrap;
  }
  .qa-primary, .qa-ghost {
    flex: 1 1 140px;
  }
}
